import { toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import type { Tab } from "@/features/PatientTabs/PatientTabs";
import { PatientTabType } from "@/features/PatientTabs/network/usePatientTabs";
import { trackEvent } from "@/lib/tracking";
import { cn } from "@/lib/utils";
import { isAuthenticatedAtom } from "@/store/authAtoms";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { cva } from "class-variance-authority";
import { useAtomValue } from "jotai";
import { LoaderCircle, X } from "lucide-react";
import type { MouseEvent } from "react";
import { Button } from "../../../components/ui";
import { useRemovePatientTab } from "../hooks/useRemovePatientTab";
import { isSelected as isSelectedUtil } from "../utils";
import { PatientTabTimer } from "./PatientTabTimer";

// constants used in PatientTabs.tsx for calculate how many tabs are displayed vs hidden
export const PATIENT_TAB_MIN_WIDTH = 208;
export const PATIENT_TAB_MAX_WIDTH = 320;

const tabVariants = cva(
  "relative h-8 w-full items-center px-2.5 text-xs flex gap-1.5 border-y-[1px] border-transparent group hover:bg-secondary hover:text-primary",
  {
    variants: {
      variant: {
        selected: "bg-white text-primary border-y-primary",
        light: "bg-tertiary text-primary",
        dark: "bg-inverse text-white",
      },
    },
    defaultVariants: {
      variant: "light",
    },
  },
);

interface PatientTabProps {
  className?: string;
  hideEOCTooltip?: boolean;
  tab: Tab;
  onSelected?: () => void;
}

export const PatientTab = ({
  tab,
  hideEOCTooltip,
  className = "",
  onSelected,
}: PatientTabProps) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const { patientId } = useParams({ strict: false });
  const { encounterId } = useSearch({ strict: false });
  const encounterIdStr = encounterId != null ? String(encounterId) : undefined;

  // Return null if not authenticated
  if (!isAuthenticated) {
    return null;
  }
  const isUrgent = tab.tabType === PatientTabType.URGENT;
  const isManuallyAdded = tab.tabType !== PatientTabType.MANUALLY_ADDED;
  const isPreview = tab.tabType === PatientTabType.PREVIEW;

  const isTabSelected = isSelectedUtil(tab, patientId, encounterIdStr);

  const removePatientTab = useRemovePatientTab(isTabSelected);

  const variant = isTabSelected ? "selected" : isUrgent ? "dark" : "light";
  const type = isUrgent ? "live" : "async";

  const handleCloseTab = async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent(TRACKERS.CLOSE_CHART);
    try {
      await removePatientTab.mutateAsync({
        patientId: tab.patient.id,
        encounterId: tab.encounter?.id,
      });
    } catch (error) {
      toast.error("Unable to remove patient", {
        id: "remove-patient-tab-error",
      });
    }
  };

  return (
    <Link
      data-testid={`patient-tab-${tab.patient.id} patient-tab-${type}`}
      to="/patient/$patientId/timeline"
      params={{ patientId: tab.patient.id }}
      search={{ encounterId: tab.encounter?.id }}
      className={cn(tabVariants({ variant }), className, isPreview && "italic")}
      onClick={() => {
        trackEvent(TRACKERS.SWITCH_CHARTS);
        onSelected?.();
      }}
      style={{
        minWidth: PATIENT_TAB_MIN_WIDTH,
        maxWidth: PATIENT_TAB_MAX_WIDTH,
      }}
    >
      <span className="truncate">
        <span>
          {`${tab.patient.nameFirst} ${tab.patient.nameLast}${isPreview ? " (Preview)" : ""}`}
        </span>
        {tab.hasOtherOpenTabs && tab.encounter?.condition && (
          <span> ({tab.encounter?.condition})</span>
        )}
      </span>

      <div className="flex items-center ml-auto gap-1">
        {isManuallyAdded && tab.sla?.patientWaitingSince && (
          <PatientTabTimer customerWaitingSince={tab.sla.patientWaitingSince} />
        )}

        {!isManuallyAdded && (
          <Button
            variant="icon"
            data-testid="patient-tab-close"
            onClick={handleCloseTab}
            disabled={removePatientTab.isPending}
            className={cn(
              "rounded-full w-5 h-5 p-0 flex items-center justify-center text-white group-hover:text-primary hover:bg-neutral-200",
              isUrgent && !isTabSelected ? "text-white" : "text-primary",
            )}
          >
            {removePatientTab.isPending ? (
              <LoaderCircle className="animate-spin" size={12} />
            ) : (
              <X size={12} />
            )}
          </Button>
        )}
      </div>

      {!hideEOCTooltip && tab.hasOtherOpenTabs && (
        <div className="w-full absolute bg-inverse text-white top-full left-0 transform translate-y-2 flex justify-center px-2.5 py-1.5 flex-col z-50 rounded-md gap-px opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-opacity ease-in-out">
          <span>
            {tab.patient.nameFirst} {tab.patient.nameLast}
          </span>
          <span>{tab.encounter?.condition}</span>
        </div>
      )}
    </Link>
  );
};
